// WebSocket Hook for Monitoring
// Provides real-time monitoring updates via Socket.IO connection to /monitoring namespace

import { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';

/**
 * Socket.IO hook for monitoring real-time updates
 * Connects to the /monitoring namespace on the API server
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoConnect - Auto-connect on mount (default: true)
 * @param {number} options.reconnectAttempts - Max reconnect attempts (default: 5)
 * @param {Function} options.onMessage - Message handler callback for alarm events
 * @returns {Object} Socket state and methods
 */
export const useMonitoringWebSocket = (options = {}) => {
  const {
    autoConnect = true,
    reconnectAttempts = 5,
    onMessage: externalOnMessage
  } = options;

  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [lastMessage, setLastMessage] = useState(null);
  const [error, setError] = useState(null);
  const [clientCount, setClientCount] = useState(0);

  const socketRef = useRef(null);
  const shouldConnectRef = useRef(autoConnect);

  // Use a ref for the external callback so the socket handlers always call the latest version
  // without causing reconnections when the callback reference changes
  const onMessageRef = useRef(externalOnMessage);
  onMessageRef.current = externalOnMessage;

  // Get Socket.IO URL
  const getSocketUrl = useCallback(() => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';
    return API_BASE_URL;
  }, []);

  // Handle incoming messages - stable reference since it reads from ref
  const handleMessage = useCallback((eventType, data) => {
    console.log('[MonitoringWebSocket] Event received:', eventType, data);

    const message = {
      type: eventType,
      ...data
    };

    setLastMessage(message);

    // Call external message handler if provided
    if (onMessageRef.current) {
      onMessageRef.current(message);
    }
  }, []);

  // Connect to Socket.IO
  const connect = useCallback(() => {
    if (socketRef.current?.connected) {
      console.log('[MonitoringWebSocket] Already connected');
      return;
    }

    try {
      const socketUrl = getSocketUrl();
      console.log('[MonitoringWebSocket] Connecting to:', socketUrl, '/monitoring');

      const socket = io(`${socketUrl}/monitoring`, {
        transports: ['websocket', 'polling'],
        reconnectionAttempts: reconnectAttempts,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 10000,
        autoConnect: true
      });

      // Connection events
      socket.on('connect', () => {
        console.log('[MonitoringWebSocket] Connected, socket ID:', socket.id);
        setConnectionStatus('connected');
        setError(null);
      });

      socket.on('connected', (data) => {
        console.log('[MonitoringWebSocket] Server confirmed connection:', data);
      });

      socket.on('subscribed', (data) => {
        console.log('[MonitoringWebSocket] Subscribed to channel:', data.channel);
      });

      socket.on('disconnect', (reason) => {
        console.log('[MonitoringWebSocket] Disconnected:', reason);
        setConnectionStatus('disconnected');
      });

      socket.on('connect_error', (err) => {
        console.log('[MonitoringWebSocket] Connection error:', err.message);
        setConnectionStatus('error');
        setError(err.message);
      });

      socket.on('reconnect_attempt', (attemptNumber) => {
        console.log('[MonitoringWebSocket] Reconnect attempt:', attemptNumber);
        setConnectionStatus('reconnecting');
      });

      socket.on('reconnect', (attemptNumber) => {
        console.log('[MonitoringWebSocket] Reconnected after', attemptNumber, 'attempts');
        setConnectionStatus('connected');
        setError(null);
      });

      socket.on('reconnect_failed', () => {
        console.log('[MonitoringWebSocket] Reconnect failed, max attempts reached');
        setConnectionStatus('unavailable');
        setError('Unable to connect to monitoring service');
      });

      // Alarm events
      socket.on('alarm_created', (data) => handleMessage('alarm_created', data));
      socket.on('alarm_update', (data) => handleMessage('alarm_update', data));
      socket.on('alarm_status_change', (data) => handleMessage('alarm_status_change', data));
      socket.on('alarm_closed', (data) => handleMessage('alarm_closed', data));

      // Device events
      socket.on('device_status_changed', (data) => handleMessage('device_status_changed', data));
      socket.on('device_heartbeat', (data) => handleMessage('device_heartbeat', data));

      // Alert events
      socket.on('alert_created', (data) => handleMessage('alert_created', data));
      socket.on('alert_updated', (data) => handleMessage('alert_updated', data));
      socket.on('alert_resolved', (data) => handleMessage('alert_resolved', data));

      // Service events
      socket.on('service_status_change', (data) => handleMessage('service_status_change', data));
      socket.on('monitoring_event', (data) => handleMessage('monitoring_event', data));

      socketRef.current = socket;
    } catch (err) {
      console.error('[MonitoringWebSocket] Connection failed:', err.message);
      setError(err.message);
      setConnectionStatus('error');
    }
  }, [getSocketUrl, reconnectAttempts, handleMessage]);

  // Disconnect from Socket.IO
  const disconnect = useCallback(() => {
    shouldConnectRef.current = false;

    if (socketRef.current) {
      console.log('[MonitoringWebSocket] Disconnecting...');
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setConnectionStatus('disconnected');
  }, []);

  // Subscribe to specific room (location or device)
  const subscribe = useCallback((options = {}) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('subscribe', options);
    } else {
      console.warn('[MonitoringWebSocket] Cannot subscribe, not connected');
    }
  }, []);

  // Unsubscribe from specific room
  const unsubscribe = useCallback((options = {}) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('unsubscribe', options);
    }
  }, []);

  // Send custom message
  const sendMessage = useCallback((event, data) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, data);
    } else {
      console.warn('[MonitoringWebSocket] Cannot send message, not connected');
    }
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      shouldConnectRef.current = true;
      connect();
    }

    // Cleanup on unmount
    return () => {
      shouldConnectRef.current = false;
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [autoConnect, connect]);

  return {
    connectionStatus,
    lastMessage,
    error,
    clientCount,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    sendMessage,
    isConnected: connectionStatus === 'connected',
    isUnavailable: connectionStatus === 'unavailable' || connectionStatus === 'error',
    socket: socketRef.current
  };
};

export default useMonitoringWebSocket;
